import type { Timestamp } from "firebase/firestore";

export type LeadStatus =
  | "new"
  | "previewing"
  | "ready"
  | "preview_sent"
  | "follow_up"
  | "converted"
  | "lost";

export type LeadKind = "minime" | "pet_keychain" | "duo" | "head_keychain";

/** One thing to be modelled: a dog, a person. One subject is one piece they
 *  would buy. The canonical comment is in functions/src/lib/types.ts. */
export interface Subject {
  id: string;
  label: string;
  /** Which product this piece is: it picks the prompt, the price on its card and
   *  the listing its order button points at. Absent on a piece written before
   *  pieces carried their own, which reads as the lead's. */
  kind?: LeadKind;
  hasPet?: boolean;
  /** How many animals are in it, up to MAX_PETS. Absent on a piece written
   *  before the count existed: read it through petsOf(). */
  pets?: number;
  /** What is added to the drawing: read it through extrasOf(). */
  extras?: Extra[];
}

/** Something added to the drawing on top of the product. The canonical comment
 *  is in functions/src/lib/types.ts. */
export type Extra = "christmas_hat" | "christmas_sweater";

export const EXTRAS: Extra[] = ["christmas_hat", "christmas_sweater"];

export const EXTRA_LABEL: Record<Extra, string> = {
  christmas_hat: "Christmas hat",
  christmas_sweater: "Christmas sweater",
};

/** Which extras a product takes: the hat on everything, the sweater on all but
 *  the head keychain, which has no body. The same rule as the backend's. */
export function extrasFor(kind: LeadKind | null | undefined): Extra[] {
  return kind === "head_keychain" ? ["christmas_hat"] : EXTRAS;
}

/** Which extras it is drawn with, limited to what the product takes, always in
 *  EXTRAS order. The same rule as the backend's extrasOf(). */
export function extrasOf(x: { kind?: LeadKind | null; extras?: unknown }): Extra[] {
  if (!Array.isArray(x.extras)) return [];
  return extrasFor(x.kind ?? "minime").filter((extra) => (x.extras as unknown[]).includes(extra));
}

/** The extras in a few words, for the end of a line: "", " with a Christmas
 *  hat", " with a Christmas hat and sweater". */
export function withExtras(x: { kind?: LeadKind | null; extras?: unknown }): string {
  const extras = extrasOf(x);
  if (extras.length === 2) return " with a Christmas hat and sweater";
  return extras.length ? ` with a ${EXTRA_LABEL[extras[0]!]}` : "";
}

/** The base a statue can stand on instead of its plain one: only the
 *  Christmas base so far, chosen on the order sheet when paying on the page,
 *  and free. The canonical comment is in functions/src/lib/types.ts. */
export type BaseStyle = "christmas";

/** How many characters a Christmas base's message may be, per product and
 *  per size label ("6 cm"), as the server sends it with the page's read. */
export type BaseMaxChars = Partial<Record<LeadKind | "pet_figure", Record<string, number>>>;

/** Most animals a MiniME or Two MiniMEs is drawn holding. The same number as
 *  MAX_PETS in functions/src/lib/types.ts. */
export const MAX_PETS = 3;

/** Most animals a statue of the animals alone shows. The same number as
 *  MAX_FIGURE_PETS in functions/src/lib/types.ts. */
export const MAX_FIGURE_PETS = 4;

export type LeadSource = "website" | "instagram" | "etsy_message" | "manual";
/** The nine the site serves, in the order it lists them: "zh" is Chinese in
 *  Traditional characters, "zh-hans" in Simplified. The same list as
 *  functions/src/lib/types.ts and the early read in index.html, which
 *  check-templates holds together. */
export const LANGUAGES = ["en", "pt", "nl", "de", "fr", "it", "es", "zh", "zh-hans"] as const;
export type Language = (typeof LANGUAGES)[number];

/** The language as <html lang> wants it: Chinese with its script, so the
 *  browser draws the glyphs (and offers the translation) of the one the page
 *  is written in, where a bare "zh" leaves it to guess. */
export function htmlLangOf(language: Language): string {
  if (language === "zh") return "zh-Hant";
  if (language === "zh-hans") return "zh-Hans";
  return language;
}

/** What a price is quoted and charged in, and what an order's value is in. A
 *  second axis beside the language and independent of it; the canonical
 *  comment is in functions/src/lib/types.ts. Uppercase in our own data, and
 *  absent always means EUR: every lead, page and sale from before there was a
 *  choice is in euros, and nothing was migrated for that to stay true. */
export type Currency = "EUR" | "USD" | "GBP";

/** Every currency the code knows, in the order the studio offers them. Knowing
 *  one is not the same as offering it: the server's CURRENCIES switch and a
 *  complete price list decide that, and the server refuses the rest. */
export const CURRENCIES: Currency[] = ["EUR", "USD", "GBP"];

/** A currency code as we write it, from anything that may be one: "gbp" as
 *  Stripe writes it, GBP as a lead does. Null for anything that is not one of
 *  ours, including nothing at all, so each caller says for itself what that
 *  means. The same rule as the backend's currencyCode(). */
export function currencyCode(value: unknown): Currency | null {
  if (typeof value !== "string") return null;
  const code = value.trim().toUpperCase() as Currency;
  return CURRENCIES.includes(code) ? code : null;
}

/** How the site chose the currency it showed, as the form sent it; "admin"
 *  once the owner changed it on the lead, "manual" on a lead typed in by hand. */
export type CurrencyWhy = "cookie" | "param" | "timezone" | "browser" | "default" | "admin" | "manual";

/** Where the euros of an amount in another currency came from: "same" for
 *  euros, "stripe" for what Stripe credited for a sale on the page, "ecb" for
 *  the ECB rate of the day before on the Lisbon calendar. The canonical comment
 *  is in functions/src/lib/types.ts. */
export type FxSource = "same" | "stripe" | "ecb";

/** What a lead's prices are in: the currency it was frozen with, or EUR on
 *  every lead from before there was one. The same rule as the backend's
 *  leadCurrency(), and like it only a reading: whether that currency is still
 *  on offer is the server's to say. */
export function leadCurrency(lead: { currency?: unknown } | null | undefined): Currency {
  return currencyCode(lead?.currency) ?? "EUR";
}

/** What a lead's order value is in: orderCurrency, or EUR where it has none,
 *  which is every value from before there was a choice. */
export function orderCurrencyOf(lead: { orderCurrency?: unknown }): Currency {
  return currencyCode(lead.orderCurrency) ?? "EUR";
}

/** A lead's order value in euros, as every total counts it. The same rule as
 *  the backend's valueEurOf() in functions/src/lib/money.ts, so the numbers
 *  page, the Ask page and the server agree on every sale:
 *
 *  - the value itself when it is in euros, which includes every value from
 *    before there was a choice. Never orderValueEur for euros, though the
 *    server writes it equal to the value: anything that writes the value
 *    alone, such as the functions rolled back to before there was a choice,
 *    leaves it behind, and a €40 sale corrected to €50 would go on counting
 *    €40 here;
 *  - orderValueEur for a value in pounds or dollars;
 *  - and null for one of those with no euros yet. That one is counted as not
 *    yet converted, never as euros: adding £80 to a euro total as €80 is the
 *    one mistake a second currency makes easy. */
export function valueEurOf(lead: {
  orderValue?: unknown;
  orderCurrency?: unknown;
  orderValueEur?: unknown;
}): number | null {
  if (orderCurrencyOf(lead) === "EUR") return isFiniteNumber(lead.orderValue) ? lead.orderValue : null;
  return isFiniteNumber(lead.orderValueEur) ? lead.orderValueEur : null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

const CURRENCY_SYMBOL: Record<Currency, string> = { EUR: "€", USD: "$", GBP: "£" };

/** An amount the way the history writes it: "€42.93", "$42.93", "£42.93".
 *  Takes Stripe's lowercase codes as they come, and absent as euros, so every
 *  euro line reads exactly as it always has. A code that is not ours is named
 *  rather than given a symbol it does not have ("CHF 42.93"), so nothing is
 *  ever shown as euros that was not. The same as the backend's show(), which
 *  writes the pushes. */
export function show(amount: number, currency?: unknown): string {
  const figure = amount.toFixed(2);
  const code = currency == null || currency === "" ? "EUR" : currencyCode(currency);
  if (code) return `${CURRENCY_SYMBOL[code]}${figure}`;
  return `${String(currency).trim().toUpperCase()} ${figure}`;
}

export type ArtifactKind = "photo" | "mockup" | "model" | "video";
export type EmailTemplate =
  | "ack"
  | "admin_new_lead"
  | "admin_preview_ready"
  | "admin_change_request"
  | "preview"
  | "follow_up"
  | "follow_up_2"
  | "post_click"
  | "change_ack"
  | "redone"
  | "day_after"
  | "order_steps"
  | "order_help"
  | "late";

/** The studio's view of a lead.
 *
 *  This used to be a much smaller subset of what the backend writes, which is
 *  why so much of it is optional: every field below the fold arrived after some
 *  leads already existed, and a lead written before a field existed simply does
 *  not have it. The rule the preview document already states applies here too:
 *  every reader treats them as optional and renders nothing without them.
 *  The canonical shape is functions/src/lib/types.ts. */
/** What a reading of the photos found in one of them. Yours only. */
export interface TriagePhoto {
  artifactId: string;
  people: number;
  pets: number;
  subject: string;
  /** Two different things in this one photo, so it has to be cut before either
   *  can be drawn. The thumbnail says so, and the piece that has to come out of
   *  it usually carries the box to cut it by: see `crop` below. The reading
   *  makes that cut by itself. */
  twoSubjects: boolean;
  note: string;
}

/** Where a piece is inside one photo, in fractions of the whole photo, with x
 *  and y the top left corner. The same four numbers the crop sheet draws with,
 *  so it can open on this rectangle unchanged. */
export interface TriageCrop {
  artifactId: string;
  box: { x: number; y: number; w: number; h: number };
}

/** One piece a reading proposed, and the reason it gave. */
export interface TriagePiece {
  kind: LeadKind;
  hasPet: boolean;
  /** Absent on a reading taken before the model counted them. */
  pets?: number;
  photos: string[];
  why: string;
  /** Where to cut this piece out of one of its photos, when it shares that
   *  photo with something else. The server cuts it as soon as the pieces are
   *  set; the studio only offers the ones it could not. Null on almost every
   *  piece, and on every reading taken before the model was asked for boxes. */
  crop?: TriageCrop | null;
  subjectId: string | null;
}

/** What the photos were read as, and whether anything was done about it.
 *  `applied` means these pieces are on the lead. The canonical shape is
 *  functions/src/lib/types.ts. */
export interface Triage {
  state: "applied" | "read" | "failed";
  pieces: TriagePiece[];
  photos: TriagePhoto[];
  confidence: "high" | "low";
  note: string;
  photosSeen: number;
  photosExpected: number;
  model: string;
  error: string | null;
  at?: Timestamp | null;
  runs: number;
  /** The photos showed fewer people or animals than the visitor asked for (or
   *  the wrong sort for a keychain), with what was asked and what was seen.
   *  Null or absent when they match. Canonical shape and rule:
   *  functions/src/lib/types.ts. */
  mismatch?: {
    missing: { people: number; pets: number };
    requested: PhotoRequest;
    seen: { people: number; pets: number };
  } | null;
}

/** What the visitor chose on the site's form, when they actually decided: the
 *  people and pets of a statue, or the one subject of a keychain. Fields they
 *  never touched are absent, so a default is never read as a choice. */
export interface PhotoRequest {
  people?: number;
  pets?: number;
  subject?: "person" | "pet";
}

/** A lead held because its photos do not show what was asked for. "flagged"
 *  waits for the customer, "rechecking" is a new set of photos being read,
 *  "resolved" is over (they added photos, accepted, or the studio drew it
 *  anyway). Canonical shape: functions/src/lib/types.ts. */
export interface PhotoCheck {
  state: "flagged" | "rechecking" | "resolved";
  at: Timestamp;
  missing: { people: number; pets: number };
  requested: PhotoRequest;
  seen: { people: number; pets: number };
  /** Customer add-photo rounds used, at most 3. */
  rounds: number;
  remindedAt?: Timestamp | null;
  resolvedAt?: Timestamp | null;
  via?: "photos" | "accepted" | "studio" | null;
}

/** Where the visit that became this lead came from, as the site resolved it
 *  on the first page of that visit. The canonical comment is in
 *  functions/src/lib/types.ts. */
export interface Attribution {
  source: string;
  medium: string;
  campaign: string;
  content: string;
  term: string;
  landingPath: string;
  referrer: string;
}

/** What drawing and sculpting this lead cost, recounted by the backend after
 *  every generation. An estimate in US dollars, not a bill. */
export interface Spend {
  drawings: number;
  unpriced: number;
  tokensIn: number;
  tokensOut: number;
  sculpts: number;
  credits: number;
  /** Every 3D on the lead, a free one from Studio included. */
  models: number;
  drawUsd: number;
  sculptUsd: number;
  usd: number;
  at?: Timestamp | null;
}

export interface Lead {
  id: string;
  token: string;
  email: string;
  name: string;
  /** What they filled the form in. */
  language: Language;
  /** What they later asked to read the preview in, if they ever asked. Every
   *  mail after the acknowledgement follows this when it is set. */
  languageChosen?: Language | null;
  languageChosenAt?: Timestamp | null;
  /** What they were shown prices in on the site, frozen when the form was
   *  sent; only the owner changes it, and only before anything is paid. Read
   *  it through leadCurrency(), which reads absent as EUR. */
  currency?: Currency;
  /** How the site chose it, or "admin" once the owner changed it. */
  currencyWhy?: CurrencyWhy;
  kind: LeadKind;
  hasPet: boolean;
  /** How many animals, as on a Subject. Absent on every lead written before the
   *  count existed: read it through petsOf(). */
  pets?: number;
  /** A statue of their animals alone, with no person (the site's "Just
   *  pets"): 1 to MAX_FIGURE_PETS animals, on a MiniME of one piece. Its pets stay 0. Read
   *  through petFigureOf(). The canonical comment is in functions/src/lib/types.ts. */
  petFigure?: number;
  /** What is added to the drawing, as on a Subject: read through extrasOf().
   *  Kept on the lead as the form sent it, so it can name an extra the lead's
   *  own product cannot wear; extrasOf() leaves that one out. */
  extras?: Extra[];
  /** How many times their page has redrawn a piece with a Christmas extra put
   *  on or taken off, counted apart from the changes they ask for. Absent is
   *  none. */
  extrasFixes?: number;
  /** The pieces this one request is for. Absent or empty means one, which is
   *  every lead written before this existed. */
  subjects?: Subject[];
  /** How many photos the front door said were coming. Only the backend uses
   *  it, to know when they have all landed. */
  photoCount?: number;
  /** What a reading of the photos made of this request, or absent when nobody
   *  has read it. Admin only, and it never reaches the preview document. */
  triage?: Triage | null;
  /** What the form asked for, when the visitor decided it, and the hold put on
   *  the drawing while the photos do not match it. */
  requested?: PhotoRequest;
  photoCheck?: PhotoCheck | null;
  product: string;
  pagePath: string;
  notes: string;
  adminNotes: string;
  status: LeadStatus;
  source?: LeadSource;
  /** Null on a lead typed in by hand, absent on every lead from before the
   *  site sent it. The numbers page keeps "not recorded" apart from "direct". */
  attribution?: Attribution | null;
  /** The key the site derived from the page the form was on. */
  productKey?: string;
  openChanges: number;
  /** The oldest unreleased piece of work on this lead, so the list can say a
   *  sent lead still has something waiting. Absent before this existed. */
  heldSince?: Timestamp | null;
  /** An express order whose address changed after the price and has not been
   *  settled: the lead is on the to-do list (functions/src/lib/types.ts). */
  expressAlert?: boolean;

  receivedAt: Timestamp;
  updatedAt?: Timestamp | null;
  previewSentAt: Timestamp | null;
  followUpSentAt?: Timestamp | null;
  etsyClickedAt: Timestamp | null;
  firstOpenedAt: Timestamp | null;
  lastOpenedAt: Timestamp | null;
  openCount: number;
  /** Which mail the page was opened from (the ?src= its links carry): the
   *  first, the latest, and a count per mail. Absent before links carried it. */
  firstOpenedVia?: string | null;
  lastOpenedVia?: string | null;
  opensVia?: Record<string, number>;
  /** The mail they had last opened the page from when they tapped to Etsy. */
  etsyClickedVia?: string | null;
  /** The 24 hours, watched: the owner's phone at about 20, the customer's
   *  "taking a little longer" past 24. Each at most once. */
  slaPingedAt?: Timestamp | null;
  lateSentAt?: Timestamp | null;
  /** The next-day mail after "I want to order" with no tap through to Etsy. */
  orderHelpSentAt?: Timestamp | null;
  whatWrongSentAt?: Timestamp | null;
  /** They unsubscribed from the follow-ups. */
  mailOptOutAt?: Timestamp | null;
  /** How far they actually got, written by /api/track. */
  heroSeenAt?: Timestamp | null;
  priceSeenAt?: Timestamp | null;
  /** When the page's newsletter offer was first half in view. */
  newsletterSeenAt?: Timestamp | null;
  /** The proof strip, the reviews and the row of their other pieces, each the
   *  first time it was half in view; and the first tap on that row. */
  proofSeenAt?: Timestamp | null;
  reviewsSeenAt?: Timestamp | null;
  suggestionsSeenAt?: Timestamp | null;
  suggestionOpenedAt?: Timestamp | null;
  /** The version of the page they were first shown, for the layout test
   *  (PageLayout), with how many pieces it had then. Written once, on the
   *  first open, by getPreview; absent on every lead first opened before. */
  layout?: (PageLayout & { pieceCount: number }) | null;
  layoutAt?: Timestamp | null;
  /** They pressed "I want to order", and when the Etsy link and the steps were
   *  last mailed to them because of it. */
  orderIntentAt?: Timestamp | null;
  orderStepsSentAt?: Timestamp | null;
  /** Paying on the page, as far as it got: offered on "I want to order", the
   *  first session asked for, Stripe's form shown or failing (and the last
   *  reason why), the longest the form was on screen before the sheet went,
   *  and how often the sheet went before it had shown. From /api/track and
   *  /api/checkout, and absent on leads from before each was recorded. */
  payHereOfferedAt?: Timestamp | null;
  checkoutStartedAt?: Timestamp | null;
  checkoutLoadedAt?: Timestamp | null;
  checkoutFailedAt?: Timestamp | null;
  checkoutFailure?: string | null;
  sheetClosedAt?: Timestamp | null;
  sheetLongestMs?: number;
  sheetClosesUnloaded?: number;
  /** The order paid for on the page, written by the Stripe webhook. Only what
   *  the numbers page and the Ask page read of it. `amount` is VAT and all, in
   *  `currency`, which is absent on every sale from before there was a choice
   *  (all of them in euros); `amountEur` is the same in euros, null while no
   *  rate is known. */
  paidOnSite?: {
    amount: number;
    fullyRefunded?: boolean;
    currency?: Currency;
    net?: number;
    amountEur?: number | null;
    fxRate?: number | null;
    fxSource?: FxSource | null;
    /** The code typed in Stripe's checkout field, if one was; `amount` is
     *  after it. The canonical comment is in functions/src/lib/types.ts
     *  (SaleDiscount). */
    discount?: {
      code: string;
      amount: number;
      partner: string | null;
    } | null;
    /** The express shipping they chose in the checkout: what it cost, VAT
     *  inside, and the VAT in it, in `currency`; already in `amount`. Absent
     *  on standard shipping. The canonical comment is in
     *  functions/src/lib/types.ts. */
    express?: {
      fee: number;
      tax: number;
      /** The final address differs from the one priced and was not settled
       *  (resolvedAt): the red pill. `shortfall` is what the new address costs
       *  more, in `currency`; absent when it could not be priced again. */
      addressChanged?: boolean;
      shortfall?: number;
      repricedCents?: number;
      /** The fee paid less the repriced price, when the final address is
       *  cheaper by a few euros or more. */
      overpaid?: number;
      /** An ISO time: the owner settled the address flag. */
      resolvedAt?: string;
      /** Only the express fee was given back: the order ships standard. */
      feeRefunded?: true;
      carrier?: string;
      jlcUsd?: number;
      jlcDutiesUsd?: number;
      city?: string;
      postcode?: string;
      units?: number;
    };
    /** The order this sale is (stripeOrders/<sessionId>). */
    sessionId?: string;
    /** How many of the first piece, and every piece of the sale, with what
     *  the multi-buy took off each line (`off`, VAT inside, in `currency`;
     *  absent when it took nothing). Only what the numbers page's multi-buy
     *  table reads of them; the canonical comment is SaleLine in
     *  functions/src/lib/types.ts. Absent on sales from before each was kept. */
    quantity?: number;
    /** Each line's piece, product and size too, and, when it has them, the
     *  Christmas base it is made on with the message to raise on it, and the
     *  extras its drawing wore when it was paid for (SaleLine). */
    lines?: {
      pieceId?: string | null;
      kind?: LeadKind;
      size?: string;
      quantity?: number;
      amount?: number;
      off?: number;
      base?: BaseStyle;
      message?: string;
      extras?: Extra[];
    }[];
    /** When the webhook first recorded it. */
    paidAt?: Timestamp | null;
    /** The mail the checkout was opened from ("follow_up", "order_help"...),
     *  null when it came from no mail; absent on sales from before it was
     *  kept. The canonical comment is in functions/src/lib/types.ts. */
    via?: string | null;
    /** A share of one payment for several of this person's previews: every
     *  figure above is this lead's alone, and this is the payment. The
     *  canonical comment is in functions/src/lib/together.ts (Together). */
    together?: {
      session: string;
      primary: string;
      leadIds: string[];
      amount: number;
      net: number;
      /** How many pieces the whole payment was. Absent before it was kept. */
      units?: number;
    } | null;
    /** The multi-buy's percentage the payment's extra pieces were priced at,
     *  and the dated window that made it ("natal-bf-2026"). Absent on a
     *  payment priced without it and on every one from before it was kept. */
    multiBuyPercent?: number;
    multiBuyWindow?: string;
  } | null;
  /** A payment completed on the page whose money has not arrived yet (a bank
   *  or voucher method), until when it is waited for. */
  paymentPendingUntil?: Timestamp | null;
  modelTurnedAt?: Timestamp | null;
  codeCopiedAt?: Timestamp | null;
  engagedAt?: Timestamp | null;
  dwellMsTotal?: number;
  trackCount?: number;
  lastEventAt?: Timestamp | null;
  /** What they said when asked, and which parts they marked. */
  feedback?: "loved" | "issue" | null;
  feedbackAt?: Timestamp | null;
  feedbackTags?: string[];
  /** The pieces they love, on a lead of several (functions Lead.lovedPieces). */
  lovedPieces?: string[];
  /** When they last said how their piece arrived, and the stars they gave it
   *  (reviews/{id}, /studio/reviews). Absent until they do. */
  reviewedAt?: Timestamp | null;
  reviewRating?: number | null;
  /** The arrival page's own token, once its link has been asked for. */
  reviewToken?: string | null;
  purgeAfter?: Timestamp | null;
  /** The follow-up machine's own marks. All optional: every lead written before
   *  they existed simply has none of them. */
  followUp2SentAt?: Timestamp | null;
  postClickSentAt?: Timestamp | null;
  dayAfterSentAt?: Timestamp | null;
  promoEndsAt?: Timestamp | null;
  purgedAt: Timestamp | null;
  /** Written by MiniME Studio, never by the cloud. */
  studioFolderName: string | null;
  convertedOrderId?: string | null;
  /** When it was first marked as ordered, and what the order came to, net of
   *  VAT and in orderCurrency, when that was said. Both absent on anything
   *  converted before they existed, until the numbers page fills the date in.
   *
   *  The value and the four fields under it are only ever written together by
   *  the server, so a value is never in one currency with euros from another.
   *  Every total adds up valueEurOf(), never orderValue itself. */
  convertedAt?: Timestamp | null;
  orderValue?: number | null;
  /** What orderValue is in. Absent or null means EUR. */
  orderCurrency?: Currency | null;
  /** orderValue in euros. Null while no rate is known for a value in another
   *  currency; absent on values from before it was kept, which were euros. */
  orderValueEur?: number | null;
  /** Euros per one unit of orderCurrency, and where the rate came from. */
  orderFxRate?: number | null;
  orderFxSource?: FxSource | null;
  spend?: Spend | null;
}

/** What a customer said once their piece arrived: reviews/{leadId}, written
 *  by functions handlers/review.ts. The same shape as Review there. */
export interface Review {
  leadId: string;
  name: string;
  email: string;
  language: Language;
  kind: LeadKind;
  /** One to five; null only when they said it arrived damaged. */
  rating: number | null;
  message: string;
  damaged: boolean;
  photos: { storagePath: string; downloadUrl: string; contentType: string; bytes: number; at: Timestamp }[];
  /** The photos went with the lead's other files, after a refund or an order
   *  taken back. */
  photosDeletedAt?: Timestamp | null;
  /** The tick that lets their photos and words onto the site, when, and the
   *  sentence they ticked, in their language. */
  showOnSite: boolean;
  consentAt: Timestamp | null;
  consentText: string | null;
  consentWithdrawnAt: Timestamp | null;
  submittedAt: Timestamp | null;
  updatedAt: Timestamp | null;
  submissions: number;
  seenAt: Timestamp | null;
}

/** Somebody who signed up in the site's newsletter box, or on their preview
 *  page: /newsletter/<hash of the address>, written by the newsletterSignup
 *  function. */
export interface NewsletterSubscriber {
  email: string;
  language: Language;
  /** The site page the box was on; "/p" for a preview page, whose own path
   *  is never stored. */
  pagePath: string;
  /** "popup" for the site's box (inline or pop-up), "preview" for the offer
   *  on a preview page. */
  source: "popup" | "preview";
  /** The sentence beside the tick they ticked. */
  consentText: string;
  signups: number;
  createdAt: Timestamp | null;
  /** The last time they signed up, which is the first time for most. */
  confirmedAt: Timestamp | null;
  /** The code this sign-up earned, which they keep: FIRSTORDER10 (10%) since
   *  the box went to 10%, "" if it offered none. Absent on rows from before, which
   *  hold FIRSTORDER15 (15%; templates.ts subscriberCode). */
  code?: string;
  /** The leads their code has been given to. */
  leadIds?: string[];
}

/** A row of the lead's own history. Written by logEvent() in the backend and,
 *  for the handful of actions the studio takes directly, by logAdminEvent()
 *  in studio/events.ts. */
export interface LeadEvent {
  id: string;
  actor: "customer" | "admin" | "system" | "openai" | "tripo";
  type: string;
  data: Record<string, unknown>;
  at: Timestamp | null;
}

/** Every mail attempt, including the ones that failed. */
export interface EmailRecord {
  id: string;
  template: EmailTemplate;
  to: string;
  subject: string;
  body: string;
  sentAt: Timestamp | null;
  error: string | null;
  createdAt: Timestamp | null;
}

export interface ChangeRequest {
  id: string;
  message: string;
  tags?: string[];
  /** The piece it was asked for from its own card; absent means the whole page. */
  pieceId?: string;
  createdAt: Timestamp | null;
  resolvedAt: Timestamp | null;
  /** What the server did with it by itself (functions/src/lib/autofix.ts):
   *  the fix it wrote from their words and queued, held for you, or why it
   *  drew nothing. Absent on a change from before it existed, or with
   *  AUTOFIX_ENABLED off. */
  autofix?: AutoFix;
  /** Answered live, straight onto their page (functions/src/lib/livefix.ts),
   *  and how that ended once every piece had: all on the page, some, or none. */
  live?: boolean;
  liveOutcome?: "live" | "partial" | "none";
  /** "extras" for a tap on their page's Christmas toggles rather than words:
   *  the piece redrawn wearing exactly `extras`. Absent on every other. */
  kind?: "extras";
  extras?: Extra[];
}

export interface AutoFix {
  state: "drafting" | "queued" | "skipped" | "failed";
  /** Queued to go straight onto their page rather than wait for you. */
  live?: boolean;
  why: string;
  fixes: {
    subjectId: string;
    label: string;
    instruction: string;
    /** The drawing the fix was made from. */
    fromId: string;
    jobId: string | null;
    /** Drawn again from the piece's photos rather than fixed: what a change
     *  that only says the face, or the likeness, is wrong gets. */
    redraw?: boolean;
    /** The detail it was drawn at, or null for the default. */
    quality?: "medium" | "high" | "xhigh" | "max" | null;
  }[];
  model: string | null;
}

export interface Artifact {
  id: string;
  kind: ArtifactKind;
  /** Which piece this is a drawing OF. Unused on a photo, which uses the list
   *  below: a drawing belongs to one piece, a photo can be several's reference. */
  subjectId?: string | null;
  subjectIds?: string[];
  origin: "customer" | "generated" | "studio" | "crop";
  storagePath: string;
  downloadUrl: string;
  contentType: string;
  bytes: number;
  position: number;
  isSelected: boolean;
  /** Made after the preview went out and not released to the client yet. The
   *  studio shows it, their page does not. Absent on everything written before
   *  this existed, which reads as not held. */
  heldAt?: Timestamp | null;
  createdAt?: Timestamp | null;
  deletedAt: Timestamp | null;

  meta: Record<string, unknown>;
}

export interface Job {
  id: string;
  kind: "mockup" | "model";
  subjectId?: string | null;
  state: "queued" | "running" | "succeeded" | "failed";
  provider: "openai" | "tripo";
  providerTaskId?: string | null;
  phase: string;
  progress: number | null;
  credits: number | null;
  error: string | null;
  /** What a correction was asked to change. Absent on a drawing from the
   *  photos, which is every drawing before fixes existed. */
  fix?: string | null;
  /** The change a queued fix answers, when it was drawn by itself. */
  changeId?: string | null;
  /** What a redraw from the page's Christmas toggles is drawn with. Absent on
   *  every other job. */
  extras?: Extra[];
  startedAt: Timestamp | null;
  finishedAt: Timestamp | null;
}

export interface PreviewArtifact {
  kind: "mockup" | "model" | "video";
  url: string;
  contentType: string;
  bytes: number;
  subjectId?: string | null;
  /** A statue's figure alone, cut out of its drawing, which BaseScene stands
   *  on the real base. Only on drawings made since it was added. */
  cutout?: Cutout;
}

/** A statue's figure alone, cut out of its drawing (functions lib/cutout.ts):
 *  WebP with its transparency, cropped to the figure, and its size in pixels. */
export interface Cutout {
  url: string;
  width: number;
  height: number;
  /** Where the figure touches the ground: left, right, height, three numbers
   *  for each foot, paw or tail, one after another (Firestore keeps no arrays in
   *  arrays), in shares of the cut-out's height, left and right from its
   *  horizontal middle, height above its lowest point. Only on cut-outs made
   *  since it was added. */
  feet?: number[];
}

/** A piece on the client's page: its product, its price, its own way out to the
 *  shop, and their own photo of it. Pieces are alternatives, not a basket, so
 *  each one is offered on its own and nothing is added up.
 *
 *  The label stays out; the photo says which one it is. The canonical shape is
 *  functions/src/lib/types.ts. */
export interface PreviewPiece {
  id: string;
  /** Absent on any preview written before pieces carried their own product,
   *  which the page reads as the lead's. */
  kind?: LeadKind;
  photoUrl?: string | null;
  price?: PriceOffer | null;
  /** How many animals, which picks the Etsy package and so the price. */
  pets?: number;
  etsyUrl?: string | null;
  /** What the drawing on the page wears now, and which Christmas extras the
   *  page may offer to put on or take off this piece today: empty outside
   *  the season, once ordered, or where a live redraw is not possible. Both
   *  absent from a server from before them, which reads as none. */
  extras?: Extra[];
  extrasOffered?: Extra[];
  /** On a page of several, what this piece is to the page (getPreview's
   *  pieceRoles): the one the page is about, another product of the same
   *  people ("See this one"), or another subject that can join the same
   *  parcel ("Add … too"). Absent from a server from before it said, and
   *  the page then works it out by the same rule (preview/primary.ts). */
  role?: PieceRole;
}

/** See PreviewPiece.role. */
export type PieceRole = "primary" | "alternative" | "extra";

/** Which version of the page this reader is shown, for the layout test
 *  (getPreview's pageLayout): a page of several as the piece they asked for
 *  with the rest in one row of suggestions, or as the cards it had before;
 *  and whether a short price line stands under the drawing. Decided by the
 *  token, so a reader always sees the same one. Absent from a server from
 *  before the test, which reads as the row without the price line. */
export interface PageLayout {
  pieces: "row" | "cards";
  priceLine: boolean;
}

/** What a piece costs, from priceFor() in functions/src/lib/templates.ts, the
 *  same numbers the checkout charges and, in euros, every mail quotes (no mail
 *  names a price in pounds or dollars). `full` and `now` are where it starts
 *  (the smallest size, before the code and with it); `sizes` is every size.
 *  With no code the two are the same number. The rest is absent on a preview
 *  written before the grid, which quoted one number, so the page renders the
 *  ladder only when it is there. */
export interface PriceOffer {
  /** A statue of the animals alone: its Christmas base is asked by "pet_figure". */
  figure?: boolean;
  full: string;
  now: string;
  code?: string;
  /** What the code takes off, in percent: 0 for a lead sent with no code.
   *  Absent on a preview written while there was only ever one code, which
   *  took 30%. */
  percent?: number;
  /** What every string here is written in. Absent on a price stored before
   *  there was a choice, which is in euros. */
  currency?: Currency;
  size?: string;
  /** "IVA incluído", "VAT included", "Tax included": what the price holds, in
   *  the reader's language, for its currency. Every price is tax-inclusive. */
  vatNote?: string;
  /** The Etsy option a piece with a pet is ordered as, e.g. "Single + Pet". */
  package?: string | null;
  /** `cents` is `now` in the currency's minor units, for adding several
   *  pieces up ("Order all"). Absent from a server from before it said.
   *
   *  The multi-buy's three (every extra piece in the same parcel at its own
   *  lower price, preview/multiBuy.ts) are there only while the server applies
   *  it to this page (CHECKOUT_MULTIBUY): `fullCents` is the list price before
   *  any code, which decides the dearest piece; `extraCents` is what one more
   *  of this size costs, the larger of the code and the multi-buy taken off,
   *  never both; `extra` is that, written as `now` is. Without them the page
   *  leaves how many to Stripe's form, as it always did.
   *
   *  Inside a dated window of the multi-buy (multiBuy.until below), and only
   *  for a lead whose code takes less off than the usual percentage,
   *  `usualExtraCents` and `usualExtra` are what one more of this size
   *  usually costs: "usually 24,74 €", never a struck price. */
  sizes?: {
    size: string;
    full: string;
    now: string;
    cents?: number;
    fullCents?: number;
    extraCents?: number;
    extra?: string;
    usualExtraCents?: number;
    usualExtra?: string;
  }[];
  /** How the page's language writes an amount in this currency,
   *  "€{{units}}.{{cents}}", so a sum is written exactly as the chips are.
   *  Absent from a server from before it said, and then no sum is shown. */
  format?: string;
  /** The multi-buy, when the server applies it to this page: what every
   *  extra piece takes off, in percent. Null when it takes nothing off any
   *  size here (a code that already takes more, as FIRSTORDER30 does), and
   *  then nothing about it is said. Absent while it is switched off.
   *
   *  Inside a dated window (the Christmas, Black Friday and Cyber Week offer,
   *  13 Nov to 2 Dec 2026) the rest is there too, and absent otherwise:
   *  - `endsOn`, the window's last day in Lisbon ("2026-12-02"), written
   *    long by the page ("2 de dezembro");
   *  - `until`, the ISO instant it ends, when the page reads itself again;
   *  - `frame`, how it is told: "christmas" up to the last day to order for
   *    Christmas, "late" (Black Friday and Cyber Week) after;
   *  - `usual`, the standing percentage it usually is ("habitualmente
   *    −25%"), absent for a lead whose code takes as much or more
   *    (FIRSTORDER30), for whom that would not be true.
   *  The canonical comment is in functions/src/lib/types.ts (PriceOffer). */
  multiBuy?: {
    percent: number;
    usual?: number;
    endsOn?: string;
    until?: string;
    frame?: "christmas" | "late";
  } | null;
}

/** What /api/preview returns. Deliberately not the Firestore document: the
 *  client is given only what it may see. */
export interface PreviewFixing {
  subjectIds: string[];
  since: string | null;
  phase: "reading" | "drawing" | "finishing";
  sketches: Record<string, { url: string; index: number; of: number }>;
}

export interface PreviewDoc {
  state: "pending" | "ready" | "purged";
  language: Language;
  kind: LeadKind;
  productLabel: string;
  firstName: string;
  artifacts: PreviewArtifact[];
  /** One entry per piece. Absent or empty on every preview written before this,
   *  which the page reads as the one piece it has always shown. */
  pieces?: PreviewPiece[];
  /** See PageLayout. */
  layout?: PageLayout | null;
  etsyUrl: string;
  /** The lead's code, or "" for a lead sent with no code (every preview sent
   *  from 6 October 2026 on): full price, nothing to copy, nothing to type.
   *  Read through hasCode(), never as a string that is always there. */
  promoCode: string;
  /** What every price on the page is in, decided by the server on every read:
   *  the lead's own currency where the page can take payment in it, and EUR
   *  everywhere else, since Etsy, the only other way to buy, charges euros.
   *  The page never works it out for itself and offers no way to change it.
   *  Absent from a server from before there was a choice, which is EUR. */
  currency?: Currency;
  /** All four are absent on any preview written before they existed, so every
   *  reader treats them as optional and renders nothing without them. */
  photoUrl?: string | null;
  price?: PriceOffer | null;
  purgeAfter?: string | null;
  /** ISO date, or absent on a preview written before the code had an expiry. */
  promoEndsAt?: string | null;
  openChanges?: number;
  /** What they answered when the page asked whether it looks right. */
  feedback?: "loved" | "issue" | null;
  /** On a page of several, the ids of the pieces they love and of those with a
   *  change open, so each piece's card answers for itself. Null or absent from
   *  a server from before pieces had cards: `feedback` and `openChanges` then
   *  answer for every piece. */
  lovedPieces?: string[] | null;
  openPieces?: string[] | null;
  /** When an order placed today would arrive, and in the weeks before
   *  Christmas the last day to order for it: the mails' own sentences, worked
   *  out by the server on every read. From the day after that last day to the
   *  24th, `late` instead: that delivery before Christmas is no longer
   *  guaranteed, or that it arrives after (the website's words); and beside
   *  it, only while the server's PRINTABLE_CARD is on, `card`, the printable
   *  card every order of those weeks is sent. Both absent the rest of the
   *  year. */
  delivery?: {
    arrives: string;
    christmas: string | null;
    late?: string;
    card?: string;
    /** What express shipping costs in the page's currency, written as the
     *  reader writes it ("€39"), for the line that says it is a choice in the
     *  checkout. Absent from a server from before express existed, and the
     *  line is then not shown. */
    expressPrice?: string;
    /** True only while the server prices express from the address typed
     *  (EXPRESS_LIVE_PRICING): the pages then show the express choice above
     *  the payment form, and the line says express starts from that price.
     *  Absent otherwise. */
    expressLive?: boolean;
    /** What an unpaid page says of express in dates: the window an express
     *  order placed today arrives in, and, only while express can still make
     *  Christmas, the Christmas sentence over the express dates. Absent on a
     *  page that has been paid and from a server from before they existed. */
    expressArrives?: string;
    expressChristmas?: string;
    /** Every country the checkout ships to, for the express picker: sent only
     *  while express is priced live. Absent from a server from before it
     *  existed, and the page lists its own shorter set. */
    expressCountries?: string[];
  } | null;
  /** The instant-preview test: pending means "being drawn, watch it appear"
   *  rather than "it will come by email". Absent on ordinary leads. */
  instant?: boolean;
  /** For an instant page still pending: whether a drawing is actually under
   *  way. False means it fell back to the studio and will come by email. */
  drawing?: boolean;
  /** The photos do not show what they asked for, and the drawing waits for
   *  them. Present only while "flagged" (their move) or "rechecking" (the new
   *  photos are being read); absent otherwise. Nothing here says what was seen
   *  or what the reading wrote, only what is missing and what they can do:
   *  `canAdd` and `roomLeft` for more photos, `continueAs` for what Continue
   *  will make (null when nothing drawable was seen, so it is not offered). */
  photoCheck?: {
    state: "flagged" | "rechecking";
    missing: { people: number; pets: number };
    requested: PhotoRequest;
    kind: "statue" | "keychain";
    canAdd: boolean;
    roomLeft: number;
    continueAs: "minime" | "duo" | "pet_keychain" | "head_keychain" | "pet_figure" | null;
    continuePets: number;
  };
  /** ISO time the request arrived, for the waiting screen's bar. */
  drawingSince?: string | null;
  /** Which part of the work an instant preview has reached. */
  drawingPhase?: "reading" | "drawing" | "finishing" | null;
  /** The newest rough frame of the drawing while it streams, which frame it
   *  is (0 first) and how many are coming. Absent or null until one lands. */
  drawingSketch?: { url: string; index: number; of: number } | null;
  /** A change they asked for that is being fixed while they watch: which
   *  pieces, since when, how far it has got, and each piece's newest rough
   *  frame by piece id. Null or absent when nothing is, including a change
   *  that waits for the studio. */
  fixing?: PreviewFixing | null;
  /** Whether this customer is offered "Pay here" beside Etsy. Decided by the
   *  server on every read (CHECKOUT_AUDIENCE); absent means no. */
  checkout?: boolean;
  /** Already paid for on this page (and not refunded): the page thanks them
   *  instead of offering to order again. */
  ordered?: boolean;
  /** Where that paid order has got to, as an ISO time for when the step last
   *  changed. Null or absent until the studio has reported it, and on any page
   *  not paid for here: the page then shows no steps. */
  fulfilment?: { step: string; at: string } | null;
  /** A payment on this page was completed but has not been paid yet (a bank
   *  or voucher method): the page says it is being confirmed and offers no
   *  second checkout. */
  processing?: boolean;
  /** The newsletter's offer, on a page that holds no code and could still be
   *  given one: what it takes off, and what the price would start from with
   *  it, in the page's language and currency. Null or absent: not offered. */
  newsletter?: { percent: number; from: string } | null;
  /** The Christmas season on this page, decided by the server on every read:
   *  whether the hat and sweater toggles are shown at all (each piece then
   *  says which in extrasOffered), and the Christmas base, only on a page
   *  offered "Pay here" and only in its own window, with how many characters
   *  its message may be per product and size. Null or absent: none of it. */
  christmas?: { extras: boolean; base: { maxChars: BaseMaxChars; wish?: { message: string } } | null } | null;
}

export const STATUS_LABEL: Record<LeadStatus, string> = {
  new: "New",
  previewing: "Working",
  ready: "Ready to send",
  preview_sent: "Sent",
  follow_up: "Nudged",
  converted: "Ordered",
  lost: "Closed",
};

export const STATUS_PILL: Record<LeadStatus, string> = {
  new: "pill-new",
  previewing: "pill-working",
  ready: "pill-working",
  preview_sent: "pill-sent",
  follow_up: "pill-sent",
  converted: "pill-sent",
  lost: "pill-quiet",
};

/** How many pieces a lead is for. The same rule as the backend's pieces():
 *  absent and empty both mean one. */
export function pieceCount(lead: { subjects?: Subject[] | null }): number {
  return Math.max(1, lead.subjects?.length ?? 0);
}

/** The pieces themselves, with the implicit one standing in when there is no
 *  array, so a caller can loop rather than branch. Its id is "", which is what
 *  sid() below turns the null subjectId of every older artifact into. */
export function subjectsOf(lead: {
  subjects?: Subject[] | null;
  kind?: LeadKind;
  hasPet?: boolean;
  pets?: number;
  extras?: Extra[];
}): Subject[] {
  const kind = lead.kind ?? "minime";
  const list = lead.subjects ?? [];
  if (!list.length) {
    const pets = petsOf({ kind, hasPet: lead.hasPet, pets: lead.pets });
    return [{ id: "", label: "", kind, hasPet: pets > 0, pets, extras: extrasOf({ kind, extras: lead.extras }) }];
  }
  // A piece written before pieces carried their own product, or their own
  // pets, is the lead's. The same filling-in the backend does; see
  // functions/src/lib/types.ts.
  return list.map((piece) => {
    const said = piece.pets != null || piece.hasPet != null;
    const pets = petsOf({
      kind: piece.kind ?? kind,
      hasPet: said ? piece.hasPet : lead.hasPet,
      pets: said ? piece.pets : lead.pets,
    });
    return {
      id: piece.id,
      label: piece.label ?? "",
      kind: piece.kind ?? kind,
      hasPet: pets > 0,
      pets,
      extras: extrasOf({ kind: piece.kind ?? kind, extras: piece.extras ?? lead.extras }),
    };
  });
}

/** How many animals a lead or a piece is drawn with, from 0 to MAX_PETS. The
 *  count is absent on everything written before it existed, where the flag
 *  alone stands for one, a pet keychain is always one and a head keychain is
 *  always none. The same rule as the backend's petsOf(). */
export function petsOf(x: {
  kind?: LeadKind | null;
  hasPet?: boolean | null;
  pets?: number | null;
}): number {
  if (x.kind === "pet_keychain") return 1;
  if (x.kind === "head_keychain") return 0;
  const n = Number(x.pets);
  if (x.pets != null && Number.isFinite(n)) return Math.min(MAX_PETS, Math.max(0, Math.round(n)));
  return x.hasPet ? 1 : 0;
}

/** Whether the product decides the pets by itself, so there is no count to
 *  choose: a pet keychain is one pet and a head keychain is none. */
export function petsFixed(kind: LeadKind | null | undefined): boolean {
  return kind === "pet_keychain" || kind === "head_keychain";
}

/** Whether the page has a code to show: a word to copy, a date it works
 *  until, a step for typing it in, a struck-through price. "" is a lead sent
 *  with no code, and so is a percent of 0; a price with no percent at all was
 *  written when the only code there was took 30%, so it still has its code.
 *  Every page sent with one keeps everything it has always shown. */
export function hasCode(code: string | null | undefined, price?: PriceOffer | null): boolean {
  return !!code && (price?.percent ?? 30) > 0;
}

/** Whether it is sold in the keychain sizes rather than the figurine ones. */
export function isKeychain(kind: LeadKind | null | undefined): boolean {
  return kind === "pet_keychain" || kind === "head_keychain";
}

/** The pets in a few words, for the end of a line: "", " with a pet", " with 2
 *  pets". Nothing for a keychain, whose name already says it. */
/** How many animals a statue of the animals alone shows, 1 to
 *  MAX_FIGURE_PETS; 0 for every other lead. Only a MiniME can be one. The same rule as the backend's
 *  petFigureOf(). */
export function petFigureOf(x: { kind?: LeadKind | null; petFigure?: number | null }): number {
  if (x.kind !== "minime" && x.kind != null) return 0;
  const n = Math.round(Number(x.petFigure));
  return Number.isFinite(n) && n > 0 ? Math.min(MAX_FIGURE_PETS, n) : 0;
}

export function withPets(x: { kind?: LeadKind | null; hasPet?: boolean | null; pets?: number | null }): string {
  if (petsFixed(x.kind)) return "";
  const pets = petsOf(x);
  return pets === 0 ? "" : pets === 1 ? " with a pet" : ` with ${pets} pets`;
}

/** Which pieces a photo is a reference for. A photo can serve several: one of
 *  two people is the duo's reference while a crop of each is a single's. */
export function refsOf(artifact: { subjectIds?: string[] | null }): string[] {
  return artifact.subjectIds ?? [];
}

/** Which photos have been cropped. On a lead of several pieces such a photo is
 *  still the reference for whatever pieces it is assigned to (cutting takes the
 *  ones it was cut for off it); on a lead of one it is left out, because the one
 *  prompt takes every photo and the crops are what it should be given. */
export function croppedSources(photos: { id: string; origin?: string; meta?: Record<string, unknown> }[]): Set<string> {
  return new Set(
    photos
      .filter((p) => p.origin === "crop")
      .map((p) => String((p.meta as { croppedFrom?: unknown })?.croppedFrom ?? ""))
      .filter(Boolean)
  );
}

/** One cut the reading is offering: which piece it is for, which photo it comes
 *  out of, and the rectangle. */
export interface SuggestedCrop {
  subjectId: string;
  photoId: string;
  box: { x: number; y: number; w: number; h: number };
}

/** The cuts a reading proposed that are still worth making.
 *
 *  Everything here is a reason one is not:
 *
 *  - A lead of one piece has nothing to separate. cropPhoto refuses a crop that
 *    is not for a piece, and rightly: the one drawing gets every photo.
 *  - A piece the reading never became, because the reading was recorded rather
 *    than applied. It has no id, so there is nothing to cut it for.
 *  - A piece that is no longer on the lead, or a photo that has gone.
 *  - A piece that already has a crop. Its cut has been made, by this or by a
 *    thumb, and offering it again would only replace it with the model's guess.
 *  - A source that is itself a crop. cropPhoto refuses to cut a cut.
 *
 *  What survives is a list the studio can show as one button. */
export function suggestedCrops(
  lead: { triage?: Triage | null; subjects?: Subject[] | null; kind?: LeadKind; hasPet?: boolean },
  photos: { id: string; origin?: string; subjectIds?: string[] | null }[]
): SuggestedCrop[] {
  const pieces = subjectsOf(lead).filter((piece) => piece.id);
  if (pieces.length < 2) return [];
  const on = new Set(pieces.map((piece) => piece.id));
  const source = new Map(photos.map((photo) => [photo.id, photo]));
  // Pieces that have one already, whoever cut it.
  const done = new Set(
    photos.filter((photo) => photo.origin === "crop").flatMap((photo) => refsOf(photo))
  );

  const out: SuggestedCrop[] = [];
  for (const piece of lead.triage?.pieces ?? []) {
    const crop = piece.crop;
    const subjectId = piece.subjectId;
    if (!crop || !subjectId || !on.has(subjectId) || done.has(subjectId)) continue;
    const photo = source.get(crop.artifactId);
    if (!photo || photo.origin === "crop") continue;
    out.push({ subjectId, photoId: photo.id, box: crop.box });
  }
  return out;
}

/** One spelling for "which piece", so null, absent and "" compare equal. */
export function sid(subjectId: string | null | undefined): string {
  return subjectId ?? "";
}

/** Waiting to be released rather than on the client's page. The mirror of the
 *  backend's isHeld, and the same reason for existing: a missing field has to
 *  read as false everywhere it is asked. */
export function isHeld(artifact: {
  heldAt?: Timestamp | null;
  deletedAt?: Timestamp | null;
}): boolean {
  return !!artifact.heldAt && !artifact.deletedAt;
}

export const KIND_LABEL: Record<LeadKind, string> = {
  minime: "MiniME",
  pet_keychain: "Pet keychain",
  duo: "Two MiniMEs",
  head_keychain: "Head keychain",
};
